// src/pages/dashboard/Transactions.tsx
import React, { useState, useEffect } from 'react';
import { getCurrentApiUrl, getCurrentBaseUrl } from '../../config/server.config';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency } from '../../lib/utils';
import { getServerUrl } from '../../config/server.config';
import {
  BitcoinIcon,
  EthereumIcon,
  TetherIcon,
  BinanceIcon,
  TronIcon,
  SearchIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExternalLinkIcon,
  RefreshCwIcon
} from '../../components/ui/Icons';
// import { blockchainExplorer } from '../../services/blockchain-explorer.service'; // Deshabilitado por CORS
import '../../assets/styles/transactions-button.css';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getTransactionHistory } from '../../store/transactionReducer'
import type { AppDispatch, RootState } from '../../store';
export default function Transactions() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  // COMENTADO: Ya no usamos transacciones globales de Redux
  // const { data: transactionsHistory, status } = useSelector((state: RootState) => state.transaction.history);
  const { data: { mainWallets, cryptoWallets } } = useSelector((state: RootState) => state.assets);
  const params = new URLSearchParams(location.search);
  const walletAddress = params.get('wallet');
  const walletCurrency = params.get('currency');

  const [selectedWallet, setSelectedWallet] = useState<string>(walletAddress || '');
  const [selectedCurrency, setSelectedCurrency] = useState<string>(walletCurrency || '');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [transactionType, setTransactionType] = useState<string>('all');
  const [transactionData, setTransactionData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOption, setHistoryOption] = useState<string>("default")
  const [quarter, setQuarter] = useState<number>(1)
  // Estado para tipo de transacción: 'fiat', 'crypto', o 'cards'
  const [transactionTypeSelected, setTransactionTypeSelected] = useState<'fiat' | 'crypto' | 'cards'>('crypto');
  const [isCardTransaction, setIsCardTransaction] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<string>('');

  // Get Zenus account from Redux store
  const { zenusAccount } = useSelector((state: RootState) => state.assets.data);
  const usdBalance = zenusAccount?.balance || 0;

  // Zenus account is now loaded from Redux store

  // Consulta para obtener las tarjetas
  const { data: cardsData, isLoading: loadingCards } = useQuery({
    queryKey: ['verestro-cards-list'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        // Usar getServerUrl para obtener la URL correcta (local o producción)
        const baseUrl = getCurrentApiUrl();

        const response = await fetch(`${baseUrl}/providers/verestro/cards`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          return [];
        }

        const data = await response.json();
        console.log("Verestro cards list response:", data);

        // Extraer la lista de tarjetas según la estructura correcta
        // El backend devuelve la estructura completa de Verestro con paginación
        if (data.success && data.data) {
          // Si data.data es directamente un array (estructura simple)
          if (Array.isArray(data.data)) {
            return data.data;
          }
          // Si data.data tiene la estructura de paginación de Verestro
          else if (data.data.data && Array.isArray(data.data.data)) {
            return data.data.data;
          }
        }
        return [];
      } catch (error) {
        console.error('Error fetching cards:', error);
        return [];
      }
    },
    staleTime: 300000, // 5 minutos
    refetchOnWindowFocus: false
  });

  // Logging de debug para wallets crypto - Solo en desarrollo
  useEffect(() => {
    if (cryptoWallets?.data && process.env.NODE_ENV === 'development') {
      console.log('Crypto wallets available:', cryptoWallets.data);
    }
  }, [cryptoWallets]);
  // COMENTADO: No usar Redux para transacciones globales
  // useEffect(() => {
  //   const info = {
  //     option: historyOption,
  //     quarter: quarter
  //   }
  //   dispatch(getTransactionHistory(info))
  // }, [historyOption, quarter, dispatch])
  // Función específica para procesar transacciones de Bitcoin
  const processBitcoinTransaction = (tx: any, walletAddress: string) => {
    const selectedCurrency = 'BTC'; // Para Bitcoin siempre será BTC
    // Identificar si la transacción es entrante o saliente
    let isIncoming = false;
    let amount = 0;
    let from = '';
    let to = '';

    // Verificar si la transacción es en formato UTXO
    if (tx.inputs && tx.out) {
      // Verificar entradas y salidas para determinar la dirección
      const inputs = tx.inputs || [];
      const outputs = tx.out || [];

      // Verificar si nuestra dirección está en las salidas (recibiendo)
      const receivingOutputs = outputs.filter((output: any) =>
        output.addr && output.addr.toLowerCase() === walletAddress.toLowerCase()
      );

      // Verificar si nuestra dirección está en las entradas (enviando)
      const sendingInputs = inputs.filter((input: any) =>
        input.prev_out && input.prev_out.addr &&
        input.prev_out.addr.toLowerCase() === walletAddress.toLowerCase()
      );

      // IMPORTANTE: Si está en los inputs, es SIEMPRE una transacción saliente
      // aunque también aparezca en los outputs (como cambio)
      if (sendingInputs.length > 0) {
        isIncoming = false;
        // Para envío, necesitamos calcular el cambio
        const totalInput = sendingInputs.reduce((sum: number, input: any) =>
          sum + (input.prev_out ? (input.prev_out.value || 0) : 0), 0
        );
        const changeOutput = outputs.find((output: any) =>
          output.addr && output.addr.toLowerCase() === walletAddress.toLowerCase()
        );
        const changeAmount = changeOutput ? (changeOutput.value || 0) : 0;

        // La cantidad enviada es la entrada total menos el cambio y la comisión
        amount = (totalInput - changeAmount) / 100000000;
        from = walletAddress;

        // Buscar la dirección de destino (no es el cambio)
        const destinationOutput = outputs.find((output: any) =>
          output.addr && output.addr.toLowerCase() !== walletAddress.toLowerCase()
        );

        if (destinationOutput && destinationOutput.addr) {
          to = destinationOutput.addr;
        }
      } else if (receivingOutputs.length > 0) {
        // Solo es entrante si NO está en los inputs
        isIncoming = true;
        amount = receivingOutputs.reduce((sum: number, output: any) => sum + (output.value || 0), 0) / 100000000;
        if (inputs[0] && inputs[0].prev_out && inputs[0].prev_out.addr) {
          from = inputs[0].prev_out.addr;
        }
        to = walletAddress;
      }
    } else if (tx.vin && tx.vout) {
      // Formato alternativo de API de Bitcoin
      // Lógica similar, adaptada a este formato
      const inputs = tx.vin || [];
      const outputs = tx.vout || [];

      // Buscar nuestra dirección en entradas y salidas
      const isInInputs = inputs.some((input: any) =>
        input.addresses && input.addresses.includes(walletAddress)
      );

      const isInOutputs = outputs.some((output: any) =>
        output.addresses && output.addresses.includes(walletAddress)
      );

      // IMPORTANTE: Si está en los inputs, es SIEMPRE saliente
      if (isInInputs) {
        // Es una transacción saliente
        isIncoming = false;

        // Encontrar las salidas que no son para nuestra dirección (destino)
        const externalOutputs = outputs.filter((output: any) =>
          !output.addresses || !output.addresses.includes(walletAddress)
        );

        amount = externalOutputs.reduce((sum: number, output: any) => sum + (output.value || 0), 0);

        from = walletAddress;

        // Obtener la dirección de destino
        if (externalOutputs[0] && externalOutputs[0].addresses && externalOutputs[0].addresses.length > 0) {
          to = externalOutputs[0].addresses[0];
        }
      } else if (isInOutputs) {
        // Es una transacción entrante (solo si NO está en los inputs)
        isIncoming = true;

        // Encontrar la cantidad recibida
        const myOutputs = outputs.filter((output: any) =>
          output.addresses && output.addresses.includes(walletAddress)
        );

        amount = myOutputs.reduce((sum: number, output: any) => sum + (output.value || 0), 0);

        // Obtener la dirección del remitente
        if (inputs[0] && inputs[0].addresses && inputs[0].addresses.length > 0) {
          from = inputs[0].addresses[0];
        }

        to = walletAddress;
      }
    } else {
      // Formato simplificado proporcionado por el backend de Belobaba
      from = tx.from || '';
      to = tx.to || '';
      const rawAmount = tx.amount || tx.value || 0;

      // IMPORTANTE: Si el amount es negativo, es una transacción SALIENTE
      if (typeof rawAmount === 'string' && rawAmount.startsWith('-')) {
        isIncoming = false;
        amount = Math.abs(parseFloat(rawAmount));
        from = walletAddress;
        // El backend pone la dirección de destino en 'to' para transacciones salientes
        to = tx.to || '';
      } else if (typeof rawAmount === 'number' && rawAmount < 0) {
        // También manejar si viene como número negativo
        isIncoming = false;
        amount = Math.abs(rawAmount);
        from = walletAddress;
        to = tx.to || '';
      } else if (selectedCurrency?.toUpperCase() === 'BTC' && !from && !to && tx.hash && rawAmount) {
        // Si no tenemos información de direcciones pero sí tenemos un hash y una cantidad positiva,
        // asumimos que es una transacción entrante para Bitcoin
        isIncoming = true;
        amount = typeof rawAmount === 'string' ? parseFloat(rawAmount) : rawAmount;
        to = walletAddress;
      } else if (to) {
        // Determinar si es entrante o saliente basado en las direcciones si existen
        isIncoming = to.toLowerCase() === walletAddress.toLowerCase();
        amount = typeof rawAmount === 'string' ? parseFloat(rawAmount) : rawAmount;
      } else {
        // Por defecto, si el amount es positivo sin más información, es entrante
        isIncoming = true;
        amount = typeof rawAmount === 'string' ? parseFloat(rawAmount) : rawAmount;
        to = walletAddress;
      }
    }

    // Construir objeto de transacción normalizado
    return {
      ...tx,
      isIncoming,
      from,
      to,
      amount,
      hash: tx.hash || tx.txid,
      time: tx.time || tx.timestamp || new Date().toISOString(),
      status: tx.confirmed ? 'CONFIRMED' : (tx.status || 'CONFIRMED')
    };
  };

  // Función para obtener transacciones - Usar backend como proxy por CORS
  const fetchTransactions = async (forceRefresh: boolean = false) => {
    if (!selectedWallet || !selectedCurrency) {
      setTransactionData([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if this is a Zenus/USD account
      if (selectedCurrency === 'USD' && selectedWallet.startsWith('zenus_')) {
        console.log('Fetching Zenus USD transactions');

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const baseUrl = getCurrentBaseUrl();

        const response = await fetch(`${baseUrl}/api/providers/zenus/transactions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Zenus transactions response:', data);

          if (data.success && data.data.transactions) {
            // Normalizar transacciones de Zenus
            const normalizedTransactions = data.data.transactions.map((tx: any) => ({
              ...tx,
              hash: tx.id || tx.reference || `zenus-${Date.now()}`,
              isIncoming: tx.type === 'CREDIT',
              from: tx.type === 'CREDIT' ? (tx.counterparty?.name || 'External') : 'Your Account',
              to: tx.type === 'DEBIT' ? (tx.counterparty?.name || 'External') : 'Your Account',
              amount: tx.amount,
              timestamp: tx.date || new Date().toISOString(),
              status: tx.status || 'COMPLETED',
              description: tx.description || ''
            }));

            setTransactionData(normalizedTransactions);
          } else {
            setTransactionData([]);
          }
        } else {
          throw new Error('Failed to fetch Zenus transactions');
        }

        setIsLoading(false);
        return;
      }

      // Determinar el endpoint correcto según la moneda
      let endpoint = '';

      // Encontrar información de la wallet para determinar la red
      const walletInfo = walletOptions.find(w =>
        w.address === selectedWallet && w.currency === selectedCurrency
      );

      const network = walletInfo?.network || '';
      const currencyUpper = selectedCurrency.toUpperCase();

      console.log(`Fetching transactions for:`);
      console.log(`  Network: ${network}`);
      console.log(`  Currency: ${currencyUpper}`);
      console.log(`  Wallet Address: ${selectedWallet}`);
      console.log(`  WalletInfo:`, walletInfo);

      // Determinar el endpoint correcto basado en la red y moneda
      // Los endpoints están bajo /api/crypto-web3/{blockchain}/wallet/{address}/transactions
      if (currencyUpper === 'BITCOIN' || currencyUpper === 'BTC') {
        endpoint = `/crypto-web3/bitcoin/wallet/${selectedWallet}/transactions`;
      }
      else if (currencyUpper === 'ETHEREUM' || currencyUpper === 'ETH') {
        endpoint = `/crypto-web3/ethereum/wallet/${selectedWallet}/transactions`;
      }
      else if (currencyUpper === 'TRON' || currencyUpper === 'TRX') {
        endpoint = `/crypto-web3/tron/wallet/${selectedWallet}/trx-transactions`;
      }
      else if (currencyUpper === 'BNB') {
        endpoint = `/crypto-web3/bnb/wallet/${selectedWallet}/transactions`;
      }
      else if (currencyUpper === 'SOLANA' || currencyUpper === 'SOL') {
        endpoint = `/crypto-web3/solana/wallet/${selectedWallet}/transactions`;
      }
      // Tokens USDT
      else if (currencyUpper.includes('USDT')) {
        if (network === 'tron') {
          endpoint = `/crypto-web3/tron/wallet/${selectedWallet}/usdt-transactions`;
        } else if (network === 'bnb' || network === 'bsc') {
          endpoint = `/crypto-web3/usdt-bep20/wallet/${selectedWallet}/transactions`;
        } else if (network === 'solana') {
          // Para Solana, usar el endpoint de SOL y filtrar por tokens después
          endpoint = `/crypto-web3/solana/wallet/${selectedWallet}/transactions`;
        } else {
          // Para Ethereum usa el endpoint general de USDT
          endpoint = `/crypto-web3/usdt/wallet/${selectedWallet}/transactions`;
        }
      }
      // Tokens USDC
      else if (currencyUpper.includes('USDC')) {
        if (network === 'solana') {
          // Para Solana, usar el endpoint de SOL y filtrar por tokens después
          endpoint = `/crypto-web3/solana/wallet/${selectedWallet}/transactions`;
        } else {
          // USDC usa un endpoint general para otras redes
          endpoint = `/crypto-web3/usdc/wallet/${selectedWallet}/transactions`;
        }
      }
      else {
        console.warn(`Currency ${selectedCurrency} on network ${network} may not be fully supported`);
        endpoint = `/crypto-web3/${network}/wallet/${selectedWallet}/transactions`;
      }

      console.log(`Fetching transactions from: ${endpoint}`);
      console.log(`NODE_ENV:`, process.env.NODE_ENV);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      // Usar getCurrentApiUrl para obtener la URL correcta según entorno
      const apiUrl = `${getCurrentApiUrl()}${endpoint}`;
      console.log(`Using API URL:`, apiUrl);
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch transaction history: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('=== API RESPONSE ===');
      console.log('Transaction data received:', data);
      console.log('Data type:', typeof data);
      console.log('Is Array?', Array.isArray(data));
      console.log('Has success property?', data && data.success);
      console.log('Has data property?', data && data.data);
      console.log('Currency being processed:', currencyUpper);
      if (data && data.success === false) {
        console.error('API returned error:', data.message);
      }
      console.log('=== END API RESPONSE ===');

      // Extraer las transacciones del formato de respuesta
      // El backend devuelve {success: true, data: Array} o directamente un Array
      let rawTransactions = [];
      if (data && typeof data === 'object' && data.success && Array.isArray(data.data)) {
        rawTransactions = data.data;
      } else if (Array.isArray(data)) {
        rawTransactions = data;
      } else if (data && data.transactions && Array.isArray(data.transactions)) {
        rawTransactions = data.transactions;
      }

      console.log('Raw transactions extracted:', rawTransactions);
      console.log('Number of transactions:', rawTransactions.length);
      if (rawTransactions.length > 0) {
        console.log('First transaction sample:', rawTransactions[0]);
      }

      // Para Solana, filtrar transacciones según el tipo de moneda seleccionada
      const currencyUp = selectedCurrency.toUpperCase();
      if (network === 'solana') {
        console.log('=== SOLANA FILTERING START ===');
        console.log('Selected currency:', currencyUp);
        console.log('Before filtering:', rawTransactions.length, 'transactions');

        if (currencyUp.includes('USDT')) {
          // Filtrar solo transacciones de USDT
          rawTransactions = rawTransactions.filter((tx: any) =>
            tx.isTokenTransfer && tx.tokenSymbol === 'USDT'
          );
          console.log('After USDT filtering:', rawTransactions.length, 'transactions');
        } else if (currencyUp.includes('USDC')) {
          // Filtrar solo transacciones de USDC
          rawTransactions = rawTransactions.filter((tx: any) =>
            tx.isTokenTransfer && tx.tokenSymbol === 'USDC'
          );
          console.log('After USDC filtering:', rawTransactions.length, 'transactions');
        } else if (currencyUp === 'SOL' || currencyUp === 'SOLANA') {
          // Filtrar solo transacciones SOL nativas (no tokens)
          rawTransactions = rawTransactions.filter((tx: any) => !tx.isTokenTransfer);
          console.log('After SOL filtering:', rawTransactions.length, 'transactions');
        }
        console.log('=== SOLANA FILTERING END ===');
      }

      // Las transacciones ya vienen filtradas correctamente del backend
      // solo necesitamos normalizarlas para la visualización
      let normalizedTransactions = [];

      if (currencyUp === 'BTC' || currencyUp === 'BITCOIN') {
        // Procesamiento específico para Bitcoin
        normalizedTransactions = rawTransactions.map((tx: any) => processBitcoinTransaction(tx, selectedWallet)).filter(Boolean);
      } else if (currencyUp === 'TRX' || currencyUp === 'TRON') {
        // Para TRON nativo (TRX)
        normalizedTransactions = rawTransactions.map((tx: any) => {
          // Determinar si es entrante o saliente basado en las direcciones
          const isIncoming = tx.to?.toLowerCase() === selectedWallet.toLowerCase() ||
                             tx.toAddress?.toLowerCase() === selectedWallet.toLowerCase();

          return {
            ...tx,
            isIncoming,
            amount: tx.amount || tx.value || '0',
            timestamp: tx.timestamp || tx.time || new Date().toISOString(),
            hash: tx.hash || tx.txID || tx.transaction_id || tx.txHash,
            from: tx.from || tx.ownerAddress || 'Unknown',
            to: tx.to || tx.toAddress || 'Unknown',
            status: tx.status || 'SUCCESS'
          };
        });
      } else if (currencyUp.includes('USDT') || currencyUp.includes('USDC')) {
        // Para tokens USDT/USDC en cualquier red
        normalizedTransactions = rawTransactions.map((tx: any) => {
          // Determinar si es entrante o saliente basado en las direcciones
          const isIncoming = tx.to?.toLowerCase() === selectedWallet.toLowerCase();

          return {
            ...tx,
            isIncoming,
            amount: tx.amount || tx.value || '0',
            timestamp: tx.timestamp || tx.time || new Date().toISOString(),
            hash: tx.hash || tx.txID || tx.transaction_id,
            from: tx.from || tx.ownerAddress || 'Unknown',
            to: tx.to || tx.toAddress || 'Unknown',
            status: tx.status || 'CONFIRMED',
            // Añadir info del token si está disponible
            tokenSymbol: tx.tokenSymbol || currencyUp.split('_')[0],
            tokenName: tx.tokenName || currencyUp
          };
        });
      } else if (currencyUp === 'BNB') {
        // Para BNB (Binance Smart Chain)
        console.log('=== BNB PROCESSING START ===');
        console.log('Raw BNB transactions:', rawTransactions);
        console.log('Selected wallet:', selectedWallet);
        normalizedTransactions = rawTransactions.map((tx: any, index: number) => {
          console.log(`Processing BNB tx ${index}:`, tx);
          // BNB puede tener formato diferente del backend
          const isIncoming = tx.to?.toLowerCase() === selectedWallet.toLowerCase();

          // Manejar timestamp que puede venir como Date object o string
          let timestamp = tx.timestamp || tx.time;
          if (timestamp instanceof Date) {
            timestamp = timestamp.toISOString();
          } else if (typeof timestamp === 'object' && timestamp !== null) {
            // Si es un objeto pero no Date, intentar convertir
            timestamp = new Date(timestamp).toISOString();
          } else if (!timestamp) {
            timestamp = new Date().toISOString();
          }

          // Determinar si es transacción de token o BNB nativo
          const isTokenTransfer = tx.isTokenTransfer || (tx.tokenSymbol && tx.tokenSymbol !== 'BNB');

          const normalizedTx = {
            ...tx,
            isIncoming,
            amount: tx.value || tx.amount || '0', // BNB usa 'value' no 'amount'
            timestamp: timestamp,
            hash: tx.hash || tx.txHash,
            from: tx.from || 'Unknown',
            to: tx.to || 'Unknown',
            status: tx.status === 1 || tx.status === '1' ? 'SUCCESS' :
                    tx.status === 0 || tx.status === '0' ? 'FAILED' :
                    (tx.status || 'CONFIRMED'),
            // BNB incluye URL de BSCScan
            explorerUrl: tx.bscscanUrl || `https://bscscan.com/tx/${tx.hash}`,
            value: tx.value || tx.amount || '0', // Mantener value para compatibilidad
            // Mantener el flag de si es token o nativo
            isTokenTransfer: isTokenTransfer,
            tokenSymbol: isTokenTransfer ? (tx.tokenSymbol || 'BEP20') : 'BNB',
            tokenName: isTokenTransfer ? (tx.tokenName || 'BEP-20 Token') : 'Binance Coin'
          };
          console.log(`Normalized BNB tx ${index}:`, normalizedTx);
          return normalizedTx;
        });
        console.log('=== BNB PROCESSING END ===');
        console.log('Final normalized BNB transactions:', normalizedTransactions);
      } else if (currencyUp === 'ETH' || currencyUp === 'ETHEREUM') {
        // Para Ethereum
        normalizedTransactions = rawTransactions.map((tx: any) => {
          const isIncoming = tx.to?.toLowerCase() === selectedWallet.toLowerCase();

          return {
            ...tx,
            isIncoming,
            amount: tx.amount || tx.value || '0',
            timestamp: tx.timestamp || tx.time || new Date().toISOString(),
            hash: tx.hash || tx.txHash,
            from: tx.from || 'Unknown',
            to: tx.to || 'Unknown',
            status: tx.status || 'CONFIRMED',
            explorerUrl: tx.etherscanUrl || `https://etherscan.io/tx/${tx.hash}`
          };
        });
      } else if (currencyUp === 'SOL' || currencyUp === 'SOLANA') {
        // Para Solana
        console.log('=== SOLANA PROCESSING START ===');
        console.log('Raw Solana transactions:', rawTransactions);
        console.log('Selected wallet:', selectedWallet);
        normalizedTransactions = rawTransactions.map((tx: any, index: number) => {
          console.log(`Processing Solana tx ${index}:`, tx);
          const isIncoming = tx.to?.toLowerCase() === selectedWallet.toLowerCase();

          const normalizedTx = {
            ...tx,
            isIncoming,
            amount: tx.amount || tx.value || '0',
            timestamp: tx.timestamp || tx.time || new Date().toISOString(),
            hash: tx.hash || tx.signature || tx.txHash,
            from: tx.from || 'Unknown',
            to: tx.to || 'Unknown',
            status: tx.status || 'CONFIRMED',
            explorerUrl: tx.solscanUrl || `https://solscan.io/tx/${tx.signature || tx.hash}`,
            // Mantener el flag de si es token o nativo
            isTokenTransfer: tx.isTokenTransfer || false,
            tokenSymbol: tx.isTokenTransfer ? (tx.tokenSymbol || 'SPL') : 'SOL',
            tokenName: tx.isTokenTransfer ? (tx.tokenName || 'SPL Token') : 'Solana'
          };
          console.log(`Normalized Solana tx ${index}:`, normalizedTx);
          return normalizedTx;
        });
        console.log('=== SOLANA PROCESSING END ===');
        console.log('Final normalized Solana transactions:', normalizedTransactions);
      } else {
        // Para otras criptomonedas, usar formato genérico
        normalizedTransactions = rawTransactions.map((tx: any) => ({
          ...tx,
          isIncoming: tx.to?.toLowerCase() === selectedWallet.toLowerCase(),
          amount: tx.amount || tx.value || '0',
          timestamp: tx.timestamp || tx.time || new Date().toISOString(),
          hash: tx.hash || tx.txHash || tx.txID,
          status: tx.status || 'CONFIRMED'
        }));
      }

      console.log('Final normalized transactions:', normalizedTransactions);
      console.log('Setting transaction data with', normalizedTransactions.length, 'transactions');
      setTransactionData(normalizedTransactions);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err instanceof Error ? err.message : String(err));
      setTransactionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para refrescar transacciones
  const handleRefreshTransactions = () => {
    fetchTransactions(true); // Force refresh bypassing cache
  };
  const getCurrentQuarter = () => {
    const currentMonth = new Date().getMonth(); // getMonth() returns 0-11
    return Math.floor(currentMonth / 3) + 1; // Calculate quarter (1-4)
  }
  // Función para obtener transacciones de tarjeta
  const fetchCardTransactions = async () => {
    if (!selectedCard) {
      setTransactionData([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      // Usar la API correcta de Verestro para obtener transacciones
      const baseUrl = getCurrentApiUrl();

      const response = await fetch(`${baseUrl}/providers/verestro/cards/${selectedCard}/transactions?page=0&size=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch card transactions: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Card transactions response:", data);

      if (data.success && data.data && data.data.transactions) {
        // Normalizar las transacciones de tarjeta para coincidir con el formato que espera la UI
        const normalizedTransactions = data.data.transactions.map((tx: any) => {
        // Para MasterCard, determinamos el tipo basado en el campo 'type'
        const isCardDeposit = tx.type === 'TOP_UP';
        const isCardPurchase = tx.type === 'PURCHASE';

          return {
            ...tx,
            hash: tx.id || tx.transactionId || tx.clientTransactionId || Date.now().toString(),
            // La lógica correcta: ahora basada en el tipo de transacción en lugar del signo del monto
            isIncoming: isCardDeposit,
            // Origen y destino según tipo de transacción
            from: tx.from || (isCardDeposit ? 'Deposit' : 'Card'),
            to: tx.to || (isCardDeposit ? 'Card' : tx.merchantName || 'Purchase'),
            // Normalizar fecha - usar transactionDate que viene del backend
            timestamp: tx.transactionDate || tx.timestamp || tx.date || new Date().toISOString(),
            date: tx.transactionDate || tx.date || tx.timestamp || new Date().toISOString(),
            status: tx.status || 'COMPLETED'
          };
        });

        setTransactionData(normalizedTransactions);
      } else {
        setTransactionData([]);
      }
    } catch (err) {
      console.error('Error fetching card transactions:', err);
      setError(err instanceof Error ? err.message : String(err));
      setTransactionData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Determinar si una transacción es entrante o saliente
  const isTransactionIncoming = (tx: any): boolean => {
    if (tx.isIncoming !== undefined) {
      console.log('Transaction has isIncoming field:', tx.isIncoming);
      return tx.isIncoming;
    }

    // Para transacciones de TRON
    if (selectedCurrency.toUpperCase() === 'TRX' ||
      selectedCurrency.toUpperCase() === 'USDT_TRON' ||
      selectedCurrency.toUpperCase() === 'USDT_TRC20') {
      if (tx.to) {
        return tx.to.toLowerCase() === selectedWallet?.toLowerCase();
      }
    }

    // Para Bitcoin, verificación adicional
    if (selectedCurrency.toUpperCase() === 'BTC') {
      // Si no tenemos el campo isIncoming pero es una transacción de Bitcoin
      // sin direcciones específicas, asumimos que es entrante
      if (!tx.from && !tx.to && tx.hash && tx.amount) {
        return true;
      }

      // Si tenemos información de direcciones, la usamos
      if (tx.to) {
        return tx.to.toLowerCase() === selectedWallet?.toLowerCase();
      }

      // Por defecto, asumimos transacción entrante para Bitcoin
      return true;
    }

    // Para otras criptomonedas
    return tx.to && tx.to.toLowerCase() === selectedWallet?.toLowerCase();
  };

  // Cargar transacciones cuando cambia la wallet o moneda seleccionada
  useEffect(() => {
    console.log('=== useEffect triggered ===');
    console.log('isCardTransaction:', isCardTransaction);
    console.log('selectedWallet:', selectedWallet);
    console.log('selectedCurrency:', selectedCurrency);
    console.log('selectedCard:', selectedCard);

    if (isCardTransaction && selectedCard) {
      // Para tarjetas, llamar fetchCardTransactions directamente
      fetchCardTransactions();
    } else if (selectedWallet && selectedCurrency && !isCardTransaction) {
      console.log('Calling fetchTransactions for crypto wallet');
      fetchTransactions(false);
    } else {
      console.log('Clearing transaction data');
      setTransactionData([]);
    }
  }, [isCardTransaction, selectedCard, selectedWallet, selectedCurrency]);
  // COMENTADO: No usar Redux para transacciones globales (duplicado)
  // useEffect(() => {
  //   const info = {
  //     option: historyOption,
  //     quarter: quarter
  //   }
  //   dispatch(getTransactionHistory(info))
  // }, [historyOption, quarter, dispatch])
  // Llamar fetchCardTransactions cuando cambie la tarjeta seleccionada
  useEffect(() => {
    if (isCardTransaction && selectedCard) {
      fetchCardTransactions();
    }
  }, [selectedCard, isCardTransaction])
  // Establecer la wallet y moneda seleccionadas si vienen como parámetros
  useEffect(() => {
    console.log('=== URL params changed ===');
    console.log('walletAddress from URL:', walletAddress);
    console.log('walletCurrency from URL:', walletCurrency);

    if (walletAddress) {
      console.log('Setting wallet from URL:', walletAddress);
      setSelectedWallet(walletAddress);
    }
    if (walletCurrency) {
      // Normalizar USDT_TRON a USDT_TRC20 si es necesario
      const normalizedCurrency = walletCurrency === 'USDT_TRON' ? 'USDT_TRC20' : walletCurrency;
      console.log('Setting currency from URL:', normalizedCurrency);
      setSelectedCurrency(normalizedCurrency);
    }
  }, [walletAddress, walletCurrency]);

  // Función para obtener icono según la moneda
  const getCurrencyIcon = (currency: string) => {
    if (isCardTransaction) {
      // Para transacciones de tarjeta, usamos un icono genérico
      return <BinanceIcon size={20} color="#B3A369" />;
    }

    const currencyUpper = currency?.toUpperCase() || '';

    if (currencyUpper.includes('BTC')) {
      return <BitcoinIcon size={20} color="#B3A369" />;
    } else if (currencyUpper.includes('ETH')) {
      return <EthereumIcon size={20} color="#B3A369" />;
    } else if (currencyUpper.includes('USDT')) {
      return <TetherIcon size={20} color="#26A17B" />;
    } else if (currencyUpper.includes('BNB')) {
      return <BinanceIcon size={20} color="#F3BA2F" />;
    } else if (currencyUpper.includes('TRX')) {
      return <TronIcon size={20} color="#FF060A" />;
    }

    return null;
  };

  // Formatear fecha para que sea más legible
  const formatDate = (dateInput: string | Date | any) => {
    if (!dateInput) return 'N/A';

    try {
      let date;

      // Si ya es un objeto Date
      if (dateInput instanceof Date) {
        date = dateInput;
      }
      // Si es un objeto con formato Date serializado de JSON
      else if (typeof dateInput === 'object' && dateInput !== null) {
        date = new Date(dateInput);
      }
      // Si es un número (timestamp)
      else if (!isNaN(Number(dateInput))) {
        // Convertir a número y crear date
        date = new Date(Number(dateInput));
      }
      // Si es un string
      else if (typeof dateInput === 'string') {
        date = new Date(dateInput);
      }
      else {
        date = new Date(dateInput);
      }

      // Verificar que la fecha es válida
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateInput);
        return 'Invalid Date';
      }

      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', e, 'Original value:', dateInput);
      return String(dateInput);
    }
  };

  // Obtener la URL del explorador de blockchain según la moneda
  const getBlockchainExplorerUrl = (currency: string, hash: string) => {
    if (isCardTransaction) {
      return ''; // No hay explorador externo para transacciones de tarjeta
    }

    const currencyUpper = currency?.toUpperCase() || '';

    if (currencyUpper.includes('BTC')) {
      return `https://www.blockchain.com/btc/tx/${hash}`;
    } else if (currencyUpper.includes('ETH')) {
      return `https://etherscan.io/tx/${hash}`;
    } else if (currencyUpper.includes('USDT')) {
      // Determinamos el explorador según la plataforma
      if (currencyUpper.includes('TRON') || currencyUpper.includes('TRC20')) {
        return `https://tronscan.org/#/transaction/${hash}`;
      } else {
        return `https://etherscan.io/tx/${hash}`;
      }
    } else if (currencyUpper.includes('BNB')) {
      return `https://bscscan.com/tx/${hash}`;
    } else if (currencyUpper.includes('TRX')) {
      return `https://tronscan.org/#/transaction/${hash}`;
    }

    return '#';
  };

  // Generar opciones de tarjeta
  const cardOptions = React.useMemo(() => {
    if (!cardsData || !Array.isArray(cardsData)) {
      return [];
    }

    return cardsData.map((card: any) => {
      let displayName = 'EUR (Mastercard)';

      // Usar lastFourDigits que viene del backend
      if (card.lastFourDigits) {
        displayName += ` (...${card.lastFourDigits})`;
      } else if (card.cardNumber) {
        displayName += ` (...${card.cardNumber.substring(card.cardNumber.length - 4)})`;
      } else if (card.maskedPan) {
        const lastFour = card.maskedPan.replace(/X/g, '').replace(/\*/g, '');
        displayName += ` (...${lastFour})`;
      }

      // Agregar estado de la tarjeta si está disponible
      if (card.status) {
        displayName += ` - ${card.status}`;
      }

      return {
        value: card.id || card.verestroCardId, // Usar id que viene de Verestro
        label: displayName,
        type: 'card'
      };
    });
  }, [cardsData]);

  // Filtrar transacciones
  const filteredTransactions = transactionData?.filter((tx: any) => {
    if (!tx) return false;

    const matchesSearch = !searchTerm ||
      (tx.hash && tx.hash.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (tx.from && tx.from.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (tx.to && tx.to.toLowerCase().includes(searchTerm.toLowerCase()));

    const isIncoming = isTransactionIncoming(tx);

    const matchesType = transactionType === 'all' ||
      (transactionType === 'incoming' && isIncoming) ||
      (transactionType === 'outgoing' && !isIncoming);

    return matchesSearch && matchesType;
  }) || [];

  console.log('Filtered transactions:', filteredTransactions);
  console.log('Transaction data state:', transactionData);
  console.log('Number of filtered transactions:', filteredTransactions.length);
  useEffect(() => {
    console.log(transactionData)
  }, [transactionData])
  // Función para exportar transacciones a CSV
  const exportToCSV = () => {
    if (!filteredTransactions.length) return;

    // Crear cabeceras para el CSV
    const headers = [
      'Type', 'Date', 'Transaction Hash', 'From/To', 'Amount', 'Status'
    ];

    // Obtener el nombre corto de la moneda sin plataforma
    let currencyName = isCardTransaction ? 'EUR' : (selectedCurrency?.split('_')[0] || '');

    // Formatear los datos para el CSV
    const csvData = filteredTransactions.map((tx: any) => {
      const isIncoming = isTransactionIncoming(tx);
      return [
        isIncoming ? 'Received' : 'Sent',
        formatDate(tx.timestamp || tx.time || new Date().toISOString()),
        tx.hash,
        isIncoming ? tx.from : tx.to,
        `${isIncoming ? '+' : '-'} ${tx.value || tx.amount} ${currencyName}`,
        tx.status || 'CONFIRMED'
      ];
    });

    // Combinar cabeceras y datos
    const csvContent = [
      headers.join(','),
      ...csvData.map((row: string[]) => row.join(','))
    ].join('\n');

    // Crear un blob y descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${isCardTransaction ? 'mastercard' : selectedCurrency}_transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Mostrar mensaje de recarga cuando hay error
  const handleReload = () => {
    if (isCardTransaction) {
      fetchCardTransactions();
    } else {
      fetchTransactions(true);
    }
  };

  // Función para normalizar la visualización del nombre de la moneda en la UI
  const formatCurrencyName = (currency: string) => {
    if (!currency) return '';

    const upperCurrency = currency.toUpperCase();

    if (upperCurrency === 'USDT_TRON') {
      return 'USDT (TRC20)';
    } else if (upperCurrency === 'USDT_TRC20') {
      return 'USDT (TRC20)';
    } else if (upperCurrency === 'USDT_ERC20') {
      return 'USDT (ERC20)';
    } else if (upperCurrency === 'USDT_BEP20') {
      return 'USDT (BEP20)';
    }

    return currency;
  };

  // Transformar la lista de wallets para el selector, usando la misma lógica que en Wallets.tsx
  const walletOptions = React.useMemo(() => {
    const result: any[] = [];

    // Procesar las wallets crypto del Redux store
    if (cryptoWallets?.data) {
      console.log('Building wallet options from cryptoWallets:', cryptoWallets.data);
      Object.keys(cryptoWallets.data).forEach(network => {
        const walletsInNetwork = cryptoWallets.data[network];
        console.log(`Processing network ${network}:`, walletsInNetwork);

        if (Array.isArray(walletsInNetwork)) {
          walletsInNetwork.forEach((wallet: any) => {
            // Process balances for each wallet
            if (wallet.balances) {
              Object.keys(wallet.balances).forEach(currency => {
                const balance = wallet.balances[currency];

                // Solo incluir wallets con balance o todas si el usuario quiere verlas
                if (balance !== undefined) {
                  // Formatear la dirección para que sea más legible
                  const shortAddress = `${wallet.address.substring(0, 8)}...${wallet.address.substring(wallet.address.length - 6)}`;

                  // Formatear el nombre de la moneda
                  let displayName = currency.toUpperCase();
                  if (currency.toUpperCase() === 'BITCOIN') displayName = 'BTC';
                  if (currency.toUpperCase() === 'ETHEREUM') displayName = 'ETH';
                  if (currency.toUpperCase() === 'TRON') displayName = 'TRX';
                  if (currency.toUpperCase() === 'SOLANA') displayName = 'SOL';

                  // Para tokens, agregar el tipo de red
                  if (currency.toUpperCase().includes('USDT')) {
                    if (network === 'ethereum') displayName = 'USDT (ERC20)';
                    else if (network === 'tron') displayName = 'USDT (TRC20)';
                    else if (network === 'bnb') displayName = 'USDT (BEP20)';
                    else if (network === 'solana') displayName = 'USDT (SPL)';
                  }
                  if (currency.toUpperCase().includes('USDC')) {
                    if (network === 'ethereum') displayName = 'USDC (ERC20)';
                    else if (network === 'tron') displayName = 'USDC (TRC20)';
                    else if (network === 'bnb') displayName = 'USDC (BEP20)';
                    else if (network === 'solana') displayName = 'USDC (SPL)';
                  }

                  const option = {
                    value: `${currency}:${wallet.address}`,
                    label: `${displayName} - ${shortAddress} (${parseFloat(balance).toFixed(6)} ${currency.toUpperCase()})`,
                    currency: currency,
                    address: wallet.address,
                    network: network,
                    balance: balance
                  };
                  console.log('Adding wallet option:', option);
                  result.push(option);
                }
              });
            }
          });
        }
      });
    }

    console.log('Final wallet options from Redux:', result);
    return result;
  }, [cryptoWallets]);

  // Opciones para FIAT wallets
  const fiatWalletOptions = React.useMemo(() => {
    const options = [];

    // Add Zenus USD account if available
    if (zenusAccount?.account) {
      options.push({
        value: `USD:zenus_${zenusAccount.account.accountNumber}`,
        label: `USD (USA) - Balance: $${usdBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        currency: 'USD',
        id: `zenus_${zenusAccount.account.accountNumber}`,
        isZenus: true
      });
    }

    // Add other FIAT wallets
    if (mainWallets?.fiatWallets?.data && Array.isArray(mainWallets.fiatWallets.data)) {
      mainWallets.fiatWallets.data.forEach((wallet: any) => {
        if (wallet && wallet.currency && wallet.id) {
          options.push({
            value: `${wallet.currency}:${wallet.id}`,
            label: `${wallet.currency} - Balance: ${wallet.balance || 0} ${wallet.currency}`,
            currency: wallet.currency,
            id: wallet.id
          });
        }
      });
    }

    return options.filter(Boolean);
  }, [mainWallets?.fiatWallets?.data, zenusAccount, usdBalance]);

  return (
    <div className='sm:p-10 h-full'>
      <div className='flex justify-between items-center mb-4'>
        <h1 style={{ fontSize: '32px', margin: 0 }}>Transactions</h1>

        <div style={{ display: 'flex', gap: '10px' }}>
          {!isCardTransaction && selectedWallet && selectedCurrency && (
            <button
              onClick={handleRefreshTransactions}
              className="transactions-btn transactions-btn-default transactions-btn-icon"
              disabled={isLoading}
              title="Refresh transactions"
            >
              <RefreshCwIcon size={16} style={{ marginRight: '5px' }} />
              Refresh
            </button>
          )}
          {filteredTransactions.length > 0 && (
            <button
              onClick={exportToCSV}
              className="transactions-btn transactions-btn-default transactions-btn-icon"
            >
              Export CSV
            </button>
          )}
        </div>
      </div>

      {!cryptoWallets ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading wallet information...</p>
        </div>
      ) : (
        <>
          {/* Filtros y selector de wallet */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            {/* Selector de tipo de transacción */}
            <div style={{ marginBottom: '15px' }}>
              <div className="transactions-selector-tabs">
                <button
                  className={`transactions-selector-tab ${transactionTypeSelected === 'fiat' ? 'active' : ''}`}
                  onClick={() => {
                    setTransactionTypeSelected('fiat');
                    setIsCardTransaction(false);
                    setSelectedCard('');
                    setSelectedWallet('');
                    setSelectedCurrency('');
                  }}
                >
                  FIAT Wallets
                </button>
                <button
                  className={`transactions-selector-tab ${transactionTypeSelected === 'crypto' ? 'active' : ''}`}
                  onClick={() => {
                    setTransactionTypeSelected('crypto');
                    setIsCardTransaction(false);
                    setSelectedCard('');
                  }}
                >
                  Crypto Wallets
                </button>
                <button
                  className={`transactions-selector-tab ${transactionTypeSelected === 'cards' ? 'active' : ''}`}
                  onClick={() => {
                    setTransactionTypeSelected('cards');
                    setIsCardTransaction(true);
                    setSelectedWallet('');
                    setSelectedCurrency('');
                  }}
                >
                  Cards
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {/* Selector condicional de wallet/card */}
              {transactionTypeSelected === 'cards' ? (
                <div style={{ flex: '1', minWidth: '250px' }}>
                  <label
                    htmlFor="card-selector"
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      color: '#666'
                    }}
                  >
                    Select Card
                  </label>
                  <select
                    id="card-selector"
                    value={selectedCard}
                    onChange={(e) => {
                      setSelectedCard(e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '5px',
                      border: '1px solid #ddd',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Select a card</option>
                    {cardOptions.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : transactionTypeSelected === 'fiat' ? (
                <div style={{ flex: '1', minWidth: '250px' }}>
                  <label
                    htmlFor="wallet-selector"
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      color: '#666'
                    }}
                  >
                    Select FIAT Wallet
                  </label>
                  <select
                    id="wallet-selector"
                    value={`${selectedCurrency}:${selectedWallet}`}
                    onChange={(e) => {
                      const [currency, id] = e.target.value.split(':');
                      console.log(`Selected FIAT wallet: ${currency}:${id}`);
                      setSelectedCurrency(currency);
                      setSelectedWallet(id);
                      navigate(`/dashboard/transactions?wallet=${id}&currency=${currency}`);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '5px',
                      border: '1px solid #ddd',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Select a FIAT wallet</option>
                    {fiatWalletOptions?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div style={{ flex: '1', minWidth: '250px' }}>
                  <label
                    htmlFor="wallet-selector"
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      color: '#666'
                    }}
                  >
                    Select Crypto Wallet
                  </label>
                  <select
                    id="wallet-selector"
                    value={`${selectedCurrency}:${selectedWallet}`}
                    onChange={(e) => {
                      if (!e.target.value) {
                        setSelectedCurrency('');
                        setSelectedWallet('');
                        return;
                      }
                      const parts = e.target.value.split(':');
                      if (parts.length >= 2) {
                        const currency = parts[0];
                        const address = parts.slice(1).join(':'); // En caso de que la dirección contenga ':'
                        console.log('=== WALLET SELECTION CHANGE ===');
                        console.log(`Selected crypto wallet - Currency: ${currency}, Address: ${address}`);
                        console.log('Setting selectedCurrency to:', currency);
                        console.log('Setting selectedWallet to:', address);

                        // Limpiar datos anteriores antes de cambiar
                        setTransactionData([]);
                        setIsLoading(true);

                        // Actualizar estados
                        setSelectedCurrency(currency);
                        setSelectedWallet(address);

                        // Navegar con los nuevos parámetros
                        navigate(`/dashboard/transactions?wallet=${address}&currency=${currency}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '5px',
                      border: '1px solid #ddd',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Select a crypto wallet</option>
                    {walletOptions?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Filtro por tipo */}
              <div style={{ flex: '1', minWidth: '250px' }}>
                <label
                  htmlFor="type-filter"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    color: '#666'
                  }}
                >
                  Transaction Type
                </label>
                <select
                  id="type-filter"
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '5px',
                    border: '1px solid #ddd',
                    fontSize: '16px'
                  }}
                >
                  <option value="all">{t('transactions.allTransactions')}</option>
                  <option value="incoming">{t('transactions.incoming')}</option>
                  <option value="outgoing">{t('transactions.outgoing')}</option>
                </select>
              </div>

              {/* Búsqueda */}
              <div style={{ flex: '1', minWidth: '250px' }}>
                <label
                  htmlFor="search"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    color: '#666'
                  }}
                >
                  Search Transactions
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  padding: '0 10px'
                }}>
                  <SearchIcon color="#666" />
                  <input
                    id="search"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by hash or address"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: 'none',
                      outline: 'none',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Botones de filtro rápido */}
            <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className={`transactions-filter-btn ${transactionType === 'all' ? 'active' : ''}`}
                onClick={() => setTransactionType('all')}
              >
                All
              </button>
              <button
                className={`transactions-filter-btn ${transactionType === 'incoming' ? 'active' : ''}`}
                onClick={() => setTransactionType('incoming')}
              >
                <ArrowDownIcon color={transactionType === 'incoming' ? 'white' : 'green'} size={14} />
                Received
              </button>
              <button
                className={`transactions-filter-btn ${transactionType === 'outgoing' ? 'active' : ''}`}
                onClick={() => setTransactionType('outgoing')}
              >
                <ArrowUpIcon color={transactionType === 'outgoing' ? 'white' : 'red'} size={14} />
                Sent
              </button>
            </div>
          </div>

          {/* Lista de transacciones */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Loading transactions...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ color: 'red' }}>Error: {error}</p>
                <button
                  onClick={handleReload}
                  className="transactions-btn transactions-btn-default"
                  style={{ marginTop: '15px' }}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                {isCardTransaction && (
                  <>
                    <div className='flex flex-wrap justify-between items-center mb-4 '>
                      <h2 style={{ fontSize: '24px' }}></h2>
                      <select
                        value={historyOption}
                        onChange={(e) => setHistoryOption(e.target.value)}
                        className="w-[11rem] text-sm !py-1"
                      >
                        <option value="default" >Default View</option>
                        <option value="more" >More Transactions</option>
                        <option value="quarter" >Quarter View</option>
                      </select>
                    </div>
                    {historyOption === "quarter" && (

                      <div className='flex flex-wrap gap-2'>
                        <button
                          onClick={() => { setQuarter(1) }}
                          className={`py-2.5 px-5 ${quarter === 1 ? "bg-green-400" : "bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
                          disabled={1 > getCurrentQuarter()}
                        >
                          1 Quarter
                        </button>
                        <button
                          onClick={() => { setQuarter(2) }}
                          className={`py-2.5 px-5 ${quarter === 2 ? "bg-green-400" : "bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
                          disabled={2 > getCurrentQuarter()}
                        >
                          2 Quarter
                        </button>
                        <button
                          onClick={() => { setQuarter(3) }}
                          className={`py-2.5 px-5 ${quarter === 3 ? "bg-green-400" : "bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
                          disabled={3 > getCurrentQuarter()}
                        >
                          3 Quarter
                        </button>
                        <button
                          onClick={() => { setQuarter(4) }}
                          className={`py-2.5 px-5 ${quarter === 4 ? "bg-green-400" : "bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
                          disabled={4 > getCurrentQuarter()}
                        >
                          4 Quarter
                        </button>
                      </div>
                    )}
                  </>
                )}
                {(isCardTransaction && selectedCard) || (!isCardTransaction && selectedWallet && selectedCurrency) ? (
                  <>
                    {(() => {
                      console.log('=== RENDERING TABLE ===');
                      console.log('filteredTransactions:', filteredTransactions);
                      console.log('filteredTransactions.length:', filteredTransactions.length);
                      return null;
                    })()}
                    {filteredTransactions.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                              <th style={{ padding: '15px 10px', textAlign: 'left' }}>Type</th>
                              <th style={{ padding: '15px 10px', textAlign: 'left' }}>Date</th>
                              <th style={{ padding: '15px 10px', textAlign: 'left' }}>Hash</th>
                              <th style={{ padding: '15px 10px', textAlign: 'left' }}>From/To</th>
                              <th style={{ padding: '15px 10px', textAlign: 'right' }}>Amount</th>
                              <th style={{ padding: '15px 10px', textAlign: 'center' }}>Status</th>
                              {!isCardTransaction && (
                                <th style={{ padding: '15px 10px', textAlign: 'center' }}>Explorer</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTransactions.map((tx: any, index: number) => {
                              if (!tx) return null;

                              const isIncoming = isTransactionIncoming(tx);
                              const amountValue = tx.value || tx.amount || '0';

                              // Obtener el nombre corto de la moneda sin plataforma
                              let currencyName = isCardTransaction ? 'EUR' : (selectedCurrency?.split('_')[0] || '');

                              // Obtener la URL del explorador si es necesaria
                              const explorerUrl = isCardTransaction ? '' : getBlockchainExplorerUrl(selectedCurrency, tx.hash);

                              return (
                                <tr
                                  key={tx.hash || index}
                                  style={{ borderBottom: '1px solid #eee' }}
                                >
                                  <td style={{ padding: '15px 10px' }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '5px'
                                    }}>
                                      {isIncoming ? (
                                        <ArrowDownIcon color="green" />
                                      ) : (
                                        <ArrowUpIcon color="red" />
                                      )}
                                      {isIncoming ? 'Received' : 'Sent'}
                                    </div>
                                  </td>
                                  <td style={{ padding: '15px 10px' }}>
                                    {formatDate(tx.timestamp || tx.time || tx.block_timestamp || tx.date || new Date().toISOString())}
                                  </td>
                                  <td style={{ padding: '15px 10px' }}>
                                    <span style={{
                                      display: 'inline-block',
                                      maxWidth: '120px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {tx.hash}
                                    </span>
                                  </td>
                                  <td style={{ padding: '15px 10px' }}>
                                    <span style={{
                                      display: 'inline-block',
                                      maxWidth: '150px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {isIncoming ? tx.from : tx.to}
                                    </span>
                                  </td>
                                  <td style={{
                                    padding: '15px 10px',
                                    textAlign: 'right',
                                    color: isIncoming ? 'green' : 'red'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      gap: '5px'
                                    }}>
                                      {!isCardTransaction && getCurrencyIcon(selectedCurrency)}
                                      {isIncoming ? '+ ' : '- '}{Math.abs(parseFloat(amountValue)).toFixed(isCardTransaction ? 2 : 6)} {currencyName}
                                    </div>
                                  </td>
                                  <td style={{ padding: '15px 10px', textAlign: 'center' }}>
                                    <span style={{
                                      backgroundColor: (tx.status === 'PENDING' || tx.status === 0) ? '#fff3cd' : '#d1e7dd',
                                      color: (tx.status === 'PENDING' || tx.status === 0) ? '#664d03' : '#0f5132',
                                      padding: '5px 10px',
                                      borderRadius: '4px',
                                      fontSize: '12px'
                                    }}>
                                      {typeof tx.status === 'number' ? (tx.status === 1 ? 'SUCCESS' : 'FAILED') : (tx.status || 'CONFIRMED')}
                                    </span>
                                  </td>
                                  {!isCardTransaction && explorerUrl && (
                                    <td style={{ padding: '15px 10px', textAlign: 'center' }}>
                                      <a
                                        href={explorerUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="transactions-btn transactions-btn-sm"
                                        style={{ color: 'white', padding: '3px 8px' }}
                                      >
                                        <ExternalLinkIcon size={14} /> View
                                      </a>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <p>No transactions found.</p>
                        {(searchTerm || transactionType !== 'all') && (
                          <button
                            onClick={() => {
                              setSearchTerm('');
                              setTransactionType('all');
                            }}
                            className="transactions-btn transactions-btn-default"
                            style={{ marginTop: '15px' }}
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Please select a {isCardTransaction ? 'card' : 'wallet'} to view transactions.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Estilos para las pestañas de selector */}
      <style>
        {`
        .transactions-selector-tabs {
          display: flex;
          border-bottom: 1px solid #ddd;
          margin-bottom: 20px;
        }
        
        .transactions-selector-tab {
          padding: 10px 20px;
          background-color: transparent;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #666;
          transition: all 0.3s;
        }
        
        .transactions-selector-tab.active {
          color: #B3A369;
          border-bottom-color: #B3A369;
        }
        
        .transactions-selector-tab:hover:not(.active) {
          color: #333;
          border-bottom-color: #ddd;
        }
      `}
      </style>
    </div>
  );
}